import { useRef, useEffect, useCallback } from "react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

import { scrollToSection } from "@/lib/scroll-to-section"

const companyLinks = [
  { label: "About", href: "#about" },
  { label: "Capability", href: "#work" },
  { label: "Use Cases", href: "#proof" },
  { label: "Team", href: "#credibility" },
]

const contactLinks = [
  { label: "contact@aexiomsemiconductors.com", href: "mailto:contact@aexiomsemiconductors.com" },
  {
    label: "Address",
    href: null,
    detail: "Plot No. 116\nDr. AS Rao Nagar,\nHyderabad - 500062\nTelangana, India",
  },
]

const legalLinks = [
  { label: "Privacy Policy", href: "#colophon" },
  { label: "Terms of Engagement", href: "#close" },
]

export function ColophonSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      if (gridRef.current) {
        const columns = gridRef.current.querySelectorAll(":scope > div")
        gsap.from(columns, {
          y: 32,
          opacity: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: gridRef.current,
            start: "top 92%",
            toggleActions: "play none none reverse",
          },
        })
      }

      if (footerRef.current) {
        gsap.from(footerRef.current, {
          y: 16,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: footerRef.current,
            start: "top 95%",
            toggleActions: "play none none reverse",
          },
        })
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  const scrollToSectionLink = useCallback((href: string) => {
    if (href.startsWith("#")) {
      scrollToSection(href)
    }
  }, [])

  return (
    <footer
      ref={sectionRef}
      id="colophon"
      className="relative border-t border-border/30 py-20 pl-6 md:pl-28 pr-6 md:pr-12"
    >
      <div ref={gridRef} className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-10">
        <div className="sm:col-span-2 lg:col-span-1">
          <p className="font-[var(--font-bebas)] text-4xl tracking-tight text-accent md:text-5xl">AEXIOM</p>
          <p className="mt-4 max-w-xs font-mono text-xs leading-relaxed text-muted-foreground md:text-sm">
            Dedicated offshore design centre for ASIC &amp; SoC physical design — RTL to GDSII.
          </p>
        </div>

        <div>
          <h4 className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Company</h4>
          <ul className="space-y-3">
            {companyLinks.map((link) => (
              <li key={link.label}>
                <button
                  type="button"
                  onClick={() => scrollToSectionLink(link.href)}
                  className="font-mono text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Contact</h4>
          <ul className="space-y-3">
            {contactLinks.map((link) => (
              <li key={link.label}>
                {link.href?.startsWith("mailto:") ? (
                  <a
                    href={link.href}
                    className="font-mono text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
                  >
                    {link.label}
                  </a>
                ) : link.href ? (
                  <button
                    type="button"
                    onClick={() => scrollToSectionLink(link.href)}
                    className="font-mono text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
                  >
                    {link.label}
                  </button>
                ) : (
                  <div className="font-mono text-sm leading-relaxed text-muted-foreground">
                    <p className="mb-1 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{link.label}</p>
                    <p className="whitespace-pre-line">{link.detail}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Legal</h4>
          <ul className="space-y-3">
            {legalLinks.map((link) => (
              <li key={link.label}>
                <button
                  type="button"
                  onClick={() => scrollToSectionLink(link.href)}
                  className="font-mono text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div
        ref={footerRef}
        className="mt-16 flex flex-col gap-4 border-t border-border/20 pt-8 md:flex-row md:items-center md:justify-between"
      >
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          © 2026 Aexiom Semiconductor. All rights reserved.
        </p>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Secure engagement · IP protected under NDA
        </p>
      </div>
    </footer>
  )
}
